import React, { useState, useEffect, useCallback } from 'react';
import { Star } from 'lucide-react';
import ReportQuestionDropdown from './ReportDropdown';
import MathText from './ui/MathText';
import CloseButton from './ui/CloseButton';
import PracticeSimilarQuestionsModal from './PracticeSimilarQuestionsModal';
import { getReportedQuestionStatusUrl, getFavoriteQuestionStatusUrl, favoriteQuestionUrl, getFavoriteQuestionPostUrl, getFavoriteQuestionDeleteUrl, getSubmissionDetailUrl, getSubmissionQuestionStatsUrl, getCustomQuizGetByIdUrl, getToeflPerformanceUrl, getIeltsPerformanceUrl, getSatPerformanceUrl, getGrePerformanceUrl } from '../utils/UrlUtilities';
import { constructReportedQuestionStatusPayload, constructFavoriteQuestionStatusPayload } from '../utils/PayloadUtils';
import { useApiCaller } from '../utils/ApiUtilities';
import { extractQuestionIds } from '../utils/BasicUtilities';
import { OPTION_KEYS, TC_BLANK_LABELS } from '../utils/Constants';
import { ClickToViewImage } from './ImageViewer';

// correct_option / selected_option are comma-joined sets (e.g. "A,C"); compare as sets.
const asOptSet = (csv) => new Set(String(csv || '').split(',').map(s => s.trim().toUpperCase()).filter(Boolean));

// IELTS scoring weight for one reviewed question (mirrors jobprep-api _ielts_question_marks):
// a "choose TWO/THREE" MCQ (correct_option has >1 letter) is worth len(correct) marks and
// counts as that many questions; earned = how many correct letters the taker selected. Every
// other question is worth 1. Only applied for IELTS model tests.
const ieltsMarks = (q) => {
    const isMcq = !['TEXT', 'TEXT_COMPLETION', 'MATCHING'].includes(q.question_type);
    const correct = asOptSet(q.correct_option);
    if (isMcq && correct.size > 1) {
        const selected = asOptSet(q.selected_option);
        let earned = 0;
        correct.forEach(l => { if (selected.has(l)) earned += 1; });
        return { max: correct.size, earned, answered: selected.size > 0 };
    }
    return { max: 1, earned: q.is_correct ? 1 : 0, answered: !!q.selected_option };
};

// Split a completion template into text + "(n)……" markers (matches the taker's MARKER_RE).
const IELTS_MARKER_RE = /(\(\d+\)(?:\s*(?:……|…|\.{2,}))?)/g;

// Read-only review of a completion block ("(n)……" markers filled with the taker's answer,
// coloured green/red, with the correct answer shown when wrong). `byNo` maps ielts.no → question.
function reviewInlineTemplate(text, byNo, keyPrefix) {
    const parts = String(text || '').split(IELTS_MARKER_RE);
    return parts.map((part, idx) => {
        const m = part && part.match(/^\((\d+)\)/);
        if (!m) return <React.Fragment key={`${keyPrefix}-t${idx}`}>{part}</React.Fragment>;
        const no = m[1];
        const q = byNo[no];
        const user = (q && q.text_answer) || '';
        const correct = (q && q.correct_text_answer) || '';
        const ok = q && q.is_correct === true;
        const color = ok ? '#065f46' : '#991b1b';
        const bg = ok ? '#d1fae5' : '#fee2e2';
        return (
            <span key={`${keyPrefix}-b${idx}`} style={{ display: 'inline-flex', alignItems: 'baseline', margin: '0 3px' }}>
                <sup style={{ color: '#6d28d9', fontWeight: 700, marginRight: 2 }}>{no}</sup>
                <span style={{ background: bg, color, borderRadius: 4, padding: '0 6px', fontWeight: 600, minWidth: '3rem', textAlign: 'center' }}>
                    {user || '—'}
                </span>
                {!ok && correct && (
                    <span style={{ color: '#065f46', marginLeft: 4, fontSize: '0.85rem' }}>✓ {correct}</span>
                )}
            </span>
        );
    });
}

const ieltsByNo = (questions) => {
    const map = {};
    questions.forEach((q) => { const no = q.metadata && q.metadata.ielts && q.metadata.ielts.no; if (no) map[no] = q; });
    return map;
};

// Per-section chronological question numbers, keyed by question id: numbering restarts at 1 for
// each section and runs across its modules → tasks → questions (blueprint order). Mirrors the
// TOEFL taker's buildNumberByQid so the review shows the same numbers the taker saw.
const numberByQidFromBlueprint = (blueprint) => {
    const map = {};
    (blueprint?.sections || []).forEach((sec) => {
        let n = 1;
        (sec.modules || []).forEach((m) => {
            (m.tasks || []).forEach((t) => {
                (t.question_ids || []).forEach((id) => { map[id] = n; n += 1; });
            });
        });
    });
    return map;
};

const QuizSubmissionDetailsModal = ({ submissionId, onClose }) => {
    const [quizSubmissionDetails, setQuizSubmissionDetails] = useState(null);
    const [blueprint, setBlueprint] = useState(null); // TOEFL model-test blueprint (for module-wise review)
    const [toeflPerf, setToeflPerf] = useState(null); // TOEFL scaled scores (0–120 + 1–6), if applicable
    const [ieltsPerf, setIeltsPerf] = useState(null); // IELTS band scores (0–9 per section + overall), if applicable
    const [satPerf, setSatPerf] = useState(null); // SAT scaled scores (per module 0–400, total 0–1600), if applicable
    const [grePerf, setGrePerf] = useState(null); // GRE scaled scores (per module 0–20, total /340), if applicable
    const [openSubjects, setOpenSubjects] = useState({});
    const [reportedQuestionDetails, setReportedQuestionDetails] = useState({});
    const [favoriteQuestionDetails, setFavoriteQuestionDetails] = useState({});
    const [loadingFavorites, setLoadingFavorites] = useState({});
    const [showExplanation, setShowExplanation] = useState({}); // { question_id: bool }
    const [questionStats, setQuestionStats] = useState(null); // { total, byId: { qid: {correct_pct,...} } }
    // Source question id for the "Practice Similar Questions" popup (null = closed).
    const [practiceForQuestionId, setPracticeForQuestionId] = useState(null);
    const CallApi = useApiCaller();

    // Lock background scroll while the modal is open.
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    useEffect(() => {
        if (!submissionId) return;
        let cancelled = false;
        (async () => {
            try {
                const url = getSubmissionDetailUrl(submissionId);
                const response = await CallApi({ url, method: 'GET', token: true });
                if (!cancelled && response.success) {
                    setQuizSubmissionDetails(response.data);
                }
            } catch (err) {
                console.error("Error fetching submission detail:", err);
            }
        })();
        return () => { cancelled = true; };
    }, [submissionId]);

    // Community per-question stats (how everyone who took this quiz answered each question).
    useEffect(() => {
        if (!submissionId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await CallApi({ url: getSubmissionQuestionStatsUrl(submissionId), method: 'GET', token: true });
                if (!cancelled && res.success) {
                    setQuestionStats({ total: res.data.total_submissions || 0, byId: res.data.stats || {} });
                }
            } catch (err) { /* stats are optional */ }
        })();
        return () => { cancelled = true; };
    }, [submissionId]);

    // Pull the quiz blueprint (if any) so a TOEFL model test can be reviewed module-by-module,
    // mirroring how it was taken, instead of one flat "subject" bucket.
    useEffect(() => {
        const qid = quizSubmissionDetails?.quiz_id;
        if (!qid) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await CallApi({ url: getCustomQuizGetByIdUrl(qid), method: 'GET', token: true });
                if (!cancelled && res.success) setBlueprint(res.data?.metadata || null);
            } catch (err) { /* blueprint is optional */ }
        })();
        return () => { cancelled = true; };
    }, [quizSubmissionDetails?.quiz_id]);

    // For a TOEFL model test, pull the section/overall scaled scores (legacy 0–120 + the 1–6
    // band) so the summary can report both scales, section-wise and overall.
    useEffect(() => {
        if (!(blueprint && blueprint.test_kind === 'toefl_model_test')) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await CallApi({ url: getToeflPerformanceUrl(submissionId), method: 'GET', token: true });
                if (!cancelled && res.success) setToeflPerf(res.data);
            } catch (err) { /* scaled scores are optional */ }
        })();
        return () => { cancelled = true; };
    }, [submissionId, blueprint]);

    // For an IELTS model test, pull the per-section + overall band scores (0–9) so the summary
    // can report Listening/Reading bands + raw totals and the overall band.
    useEffect(() => {
        if (!(blueprint && blueprint.test_kind === 'ielts_model_test')) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await CallApi({ url: getIeltsPerformanceUrl(submissionId), method: 'GET', token: true });
                if (!cancelled && res.success) setIeltsPerf(res.data);
            } catch (err) { /* band scores are optional */ }
        })();
        return () => { cancelled = true; };
    }, [submissionId, blueprint]);

    // For a SAT model test, pull the per-module scaled scores (each 0–400 → total 0–1600).
    useEffect(() => {
        if (!(blueprint && blueprint.test_kind === 'sat_model_test')) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await CallApi({ url: getSatPerformanceUrl(submissionId), method: 'GET', token: true });
                if (!cancelled && res.success) setSatPerf(res.data);
            } catch (err) { /* scaled scores are optional */ }
        })();
        return () => { cancelled = true; };
    }, [submissionId, blueprint]);

    // For a GRE model test, pull the per-module scaled scores (each 0–20; a full test totals /340).
    useEffect(() => {
        if (!(blueprint && blueprint.test_kind === 'gre_model_test')) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await CallApi({ url: getGrePerformanceUrl(submissionId), method: 'GET', token: true });
                if (!cancelled && res.success) setGrePerf(res.data);
            } catch (err) { /* scaled scores are optional */ }
        })();
        return () => { cancelled = true; };
    }, [submissionId, blueprint]);

    const fetchReportedQuestionDetails = useCallback(async () => {
        try {
            const question_ids = extractQuestionIds(quizSubmissionDetails?.questions);
            const url = getReportedQuestionStatusUrl();
            const payload = constructReportedQuestionStatusPayload(question_ids);
            const response = await CallApi({
                url: url,
                method: 'POST',
                token: true,
                payload: payload
            });
            if (response.success) {
                setReportedQuestionDetails(response.data?.question_ids ?? {});
            } else {
                console.log('Failed to fetch reported question status.');
            }
        } catch (err) {
            console.error("Error fetching reported question status:", err);
        }
    }, [quizSubmissionDetails]);

    const fetchFavoriteQuestionDetails = useCallback(async () => {
        try {
            const question_ids = extractQuestionIds(quizSubmissionDetails?.questions);
            const url = getFavoriteQuestionStatusUrl();
            const payload = constructFavoriteQuestionStatusPayload(question_ids);
            const response = await CallApi({
                url: url,
                method: 'POST',
                token: true,
                payload: payload
            });
            if (response.success) {
                // Convert to a mapping of question_id -> is_favorite
                const favoriteMap = {};
                const questionIdMap = response.data?.question_ids ?? {};
                Object.keys(questionIdMap).forEach(questionId => {
                    favoriteMap[questionId] = !!questionIdMap[questionId];
                });
                console.log("favoriteMap :: ", favoriteMap);
                setFavoriteQuestionDetails(favoriteMap);
            } else {
                console.log('Failed to fetch favorite question status.');
            }
        } catch (err) {
            console.error("Error fetching favorite question status:", err);
        }
    }, [quizSubmissionDetails]);

    const toggleFavorite = async (questionId) => {
        // Set loading state for this specific question
        setLoadingFavorites(prev => ({ ...prev, [questionId]: true }));
        
        try {
            const isFavorite = favoriteQuestionDetails[questionId];
            
            let response;
            if (!isFavorite) {
                // Add to favorites
                const url = getFavoriteQuestionPostUrl();
                response = await CallApi({
                    url: url,
                    method: 'POST',
                    token: true,
                    payload: { question_id: questionId }
                });
            } else {
                // Remove from favorites
                const url = getFavoriteQuestionDeleteUrl([questionId]);
                response = await CallApi({
                    url: url,
                    method: 'DELETE',
                    token: true
                });
            }

            if (response.success) {
                // Update local state
                setFavoriteQuestionDetails(prev => ({
                    ...prev,
                    [questionId]: !isFavorite
                }));
            } else {
                console.log('Failed to update favorite status.');
            }
        } catch (err) {
            console.error("Error toggling favorite status:", err);
        } finally {
            setLoadingFavorites(prev => ({ ...prev, [questionId]: false }));
        }
    };

    useEffect(() => {
        if (quizSubmissionDetails?.questions && Object.keys(quizSubmissionDetails?.questions).length > 0) {
            const initialOpenState = Object.keys(quizSubmissionDetails.questions).reduce((acc, subject) => {
                acc[subject] = true; // All subjects open by default
                return acc;
            }, {});
            setOpenSubjects(initialOpenState);
            fetchReportedQuestionDetails();
            fetchFavoriteQuestionDetails();
        }
    }, [quizSubmissionDetails, fetchReportedQuestionDetails, fetchFavoriteQuestionDetails]);

    const toggleSubject = (subject) => {
        setOpenSubjects(prev => ({
            ...prev,
            [subject]: !prev[subject]
        }));
    };

    if (!quizSubmissionDetails) return null;

    // Per-subject report computed from the reviewed questions (mirrors the homepage
    // report card): overall score, per-subject accuracy, and a weak-subject recommendation.
    const questionsBySubject = quizSubmissionDetails.questions || {};
    const isIelts = blueprint?.test_kind === 'ielts_model_test';
    const isToefl = blueprint?.test_kind === 'toefl_model_test';
    const isSat = blueprint?.test_kind === 'sat_model_test';
    // Per-section chronological numbers (see numberByQidFromBlueprint) for the TOEFL review, so the
    // review shows the same Q-numbers the taker saw.
    const numberByQid = numberByQidFromBlueprint(blueprint);
    // For IELTS, a "choose TWO/THREE" MCQ is worth (and counts as) len(correct) questions
    // (earned = correctly-chosen letters), matching the band raw totals; everything else is 1.
    // Non-IELTS quizzes keep the original 1-per-question count.
    const qMarks = (q) => (isIelts ? ieltsMarks(q) : { max: 1, earned: q.is_correct ? 1 : 0, answered: !!q.selected_option });
    let totalQ = 0;
    let totalCorrect = 0;
    let totalAnswered = 0;
    const subjectStats = Object.entries(questionsBySubject).map(([subject, qs]) => {
        let total = 0;
        let correct = 0;
        let answered = 0;
        qs.forEach(q => {
            const m = qMarks(q);
            total += m.max;
            correct += m.earned;
            if (m.answered) answered += m.max;
        });
        totalQ += total;
        totalCorrect += correct;
        totalAnswered += answered;
        return { subject, total, correct, answered, accuracy: total ? Math.round((100 * correct) / total) : 0 };
    });
    const overallPct = totalQ ? Math.round((100 * totalCorrect) / totalQ) : 0;
    const weakSubjects = subjectStats.filter(s => s.accuracy < 60).sort((a, b) => a.accuracy - b.accuracy);
    const accuracyColor = (pct) => (pct >= 75 ? '#16a34a' : pct >= 60 ? '#2563eb' : pct >= 40 ? '#d97706' : '#dc2626');

    // correct_option / selected_option are comma-joined sets (e.g. "A,C"); compare as sets.
    const letterOf = (optionKey) => optionKey.split('_')[1]?.toUpperCase();
    const asSet = (csv) => new Set(String(csv || '').split(',').map(s => s.trim().toUpperCase()).filter(Boolean));

    // Community stats line for one question — % correct / incorrect / not-answered across everyone
    // who took this quiz. Returns null until stats load (or when this quiz has no other attempts).
    const renderQuestionStats = (questionId) => {
        if (!questionStats) return null;
        if (!questionStats.total) {
            return <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.4rem' }}>No attempt stats yet.</div>;
        }
        const s = questionStats.byId[questionId];
        const c = s ? s.correct_pct : 0;
        const w = s ? s.incorrect_pct : 0;
        const n = s ? s.not_answered_pct : 100;
        return (
            <div style={{ marginTop: '0.5rem' }}>
                <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', background: '#e5e7eb' }}>
                    <div style={{ width: `${c}%`, background: '#22c55e' }} />
                    <div style={{ width: `${w}%`, background: '#ef4444' }} />
                    <div style={{ width: `${n}%`, background: '#9ca3af' }} />
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    <span style={{ color: '#16a34a' }}>✔ {c}% correct</span>
                    {' · '}
                    <span style={{ color: '#dc2626' }}>✘ {w}% incorrect</span>
                    {' · '}
                    <span>— {n}% not answered</span>
                    {' '}<span style={{ color: '#9ca3af' }}>({questionStats.total} attempt{questionStats.total === 1 ? '' : 's'})</span>
                </div>
            </div>
        );
    };

    const getOptionStyle = (optionKey, question) => {
        const letter = letterOf(optionKey);
        const isCorrectOption = asSet(question.correct_option).has(letter);
        const isSelectedOption = asSet(question.selected_option).has(letter);

        if (isCorrectOption) {
            return { backgroundColor: '#d1fae5', color: '#065f46', padding: '0.5rem', borderRadius: '0.25rem' }; // Green for a correct option (selected or not)
        } else if (isSelectedOption) {
            return { backgroundColor: '#fee2e2', color: '#991b1b', padding: '0.5rem', borderRadius: '0.25rem' }; // Red for a wrongly selected option
        }
        return { padding: '0.5rem', borderRadius: '0.25rem' }; // Default
    };

    // Review groups: for a TOEFL model test, group section → module (mirroring how it was taken);
    // otherwise fall back to the subject buckets. Each group = { key, label, questions }.
    const allById = {};
    Object.values(questionsBySubject).forEach(arr => arr.forEach(q => { allById[q.question_id] = q; }));
    let reviewGroups;
    if (blueprint && (blueprint.test_kind === 'toefl_model_test' || blueprint.test_kind === 'ielts_model_test' || blueprint.test_kind === 'sat_model_test') && Array.isArray(blueprint.sections)) {
        reviewGroups = [];
        const usedIds = new Set();
        blueprint.sections.forEach(sec => (sec.modules || []).forEach(mod => {
            const qids = (mod.tasks || []).flatMap(t => t.question_ids || []);
            const qs = qids.map(id => allById[id]).filter(Boolean);
            qs.forEach(q => usedIds.add(q.question_id));
            // IELTS review renders per-task (completion templates/tables, matching, etc.), so it
            // needs the module's tasks + a lookup; TOEFL keeps the flat question list.
            if (qs.length) reviewGroups.push({
                key: `${sec.key}-${mod.key}`, label: `${sec.name} — ${mod.name}`, questions: qs,
                ieltsTasks: isIelts ? (mod.tasks || []) : null,
                toeflTasks: isToefl ? (mod.tasks || []) : null,
            });
        }));
        // Any questions not referenced by the blueprint fall back to subject buckets.
        Object.entries(questionsBySubject).forEach(([subject, qs]) => {
            const leftover = qs.filter(q => !usedIds.has(q.question_id));
            if (leftover.length) reviewGroups.push({ key: `left-${subject}`, label: subject, questions: leftover });
        });
    } else {
        reviewGroups = Object.entries(questionsBySubject).map(([subject, qs]) => ({ key: subject, label: subject, questions: qs }));
    }

    // Per-group question count uses the same IELTS weighting as the totals (choose-two = N).
    const groupCount = (qs) => qs.reduce((n, q) => n + qMarks(q).max, 0);

    // Favorite (star) + Report controls for a question — shared by the flat card and the
    // IELTS/TOEFL model-test cards so every review row (any level) exposes them the same way.
    const questionControls = (q) => (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
                onClick={() => toggleFavorite(q.question_id)}
                disabled={loadingFavorites[q.question_id]}
                style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '0.3rem', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: loadingFavorites[q.question_id] ? 0.7 : 1 }}
                title={favoriteQuestionDetails[q.question_id] ? 'Remove from favorites' : 'Add to favorites'}
            >
                <Star fill={favoriteQuestionDetails[q.question_id] ? '#fbbf24' : 'transparent'} color={favoriteQuestionDetails[q.question_id] ? '#fbbf24' : '#6b7280'} size={20} />
            </button>
            <ReportQuestionDropdown questionId={q.question_id} questionReportDetails={reportedQuestionDetails[q.question_id] || {}} onClose={onClose} />
        </div>
    );

    // ---- IELTS read-only review: mirror the taker (passage/audio once, then per-task) ----
    const renderIeltsQuestionCard = (q, i) => {
        const isMcq = !['TEXT', 'MATCHING'].includes(q.question_type);
        const correct = asSet(q.correct_option);
        const multi = isMcq && correct.size > 1;
        const m = multi ? ieltsMarks(q) : null;
        const no = (q.metadata && q.metadata.ielts && q.metadata.ielts.no) || (i + 1);
        const ok = q.is_correct === true;
        const wrong = q.is_correct === false || (q.is_correct == null && (q.question_type !== 'TEXT' || !!q.correct_text_answer));
        return (
            <div key={q.question_id} style={{ marginBottom: '1rem', padding: '0.75rem', border: '1px solid #eef2ff', borderRadius: '0.5rem', background: '#f9fafb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <p style={{ color: '#1f2937', margin: 0, flex: 1 }}>
                        {(ok || wrong) && <span style={{ color: ok ? '#16a34a' : '#dc2626', fontWeight: 800, fontSize: '1.2rem', marginRight: 6, verticalAlign: 'middle' }}>{ok ? '✓' : '✗'}</span>}
                        <span style={{ fontWeight: 700, color: '#f97316', marginRight: 6 }}>Question {no}</span><MathText text={q.question_text} />
                        {multi && <span style={{ marginLeft: 8, fontSize: '0.72rem', background: '#ede9fe', color: '#5b21b6', borderRadius: 6, padding: '2px 8px', fontWeight: 700 }}>choose {correct.size} · {m.earned}/{m.max} correct</span>}
                    </p>
                    {questionControls(q)}
                </div>
                <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {q.question_type === 'TEXT' ? (
                        <>
                            <div style={{ padding: '0.5rem', borderRadius: '0.25rem', backgroundColor: q.correct_text_answer ? (ok ? '#d1fae5' : '#fee2e2') : '#eef2ff', color: q.correct_text_answer ? (ok ? '#065f46' : '#991b1b') : '#1f2937' }}>
                                <strong>Your Answer: </strong>{q.text_answer ? <MathText text={q.text_answer} /> : <span style={{ color: '#6b7280' }}>No answer</span>}
                            </div>
                            {q.correct_text_answer && (
                                <div style={{ padding: '0.5rem', borderRadius: '0.25rem', backgroundColor: '#d1fae5', color: '#065f46' }}><strong>Correct Answer: </strong><MathText text={q.correct_text_answer} /></div>
                            )}
                        </>
                    ) : (
                        <>
                            {OPTION_KEYS.map(optionKey => q[optionKey] && (
                                <div key={optionKey} style={getOptionStyle(optionKey, q)}>
                                    <span style={{ fontWeight: 'bold' }}>{optionKey.replace('option_', '').toUpperCase()}: </span><MathText text={q[optionKey]} />
                                </div>
                            ))}
                            <p style={{ marginTop: '0.25rem', fontSize: '0.85rem' }}>
                                <strong style={{ color: '#4b5563' }}>Your Answer: </strong>
                                <span style={{ color: q.selected_option ? (ok ? '#065f46' : '#991b1b') : '#dc2626', fontWeight: 700 }}>{q.selected_option || 'No selection'}</span>
                                {' · '}<strong style={{ color: '#4b5563' }}>Correct: </strong><span style={{ color: '#065f46' }}>{q.correct_option}</span>
                            </p>
                        </>
                    )}
                    {(q.answer_explanation || q.explanation_image_url) && (
                        <div>
                            <button onClick={() => setShowExplanation(prev => ({ ...prev, [q.question_id]: !prev[q.question_id] }))}
                                style={{ backgroundColor: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: '0.375rem', padding: '0.3rem 0.6rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                                {showExplanation[q.question_id] ? 'Hide Explanation' : 'Show Explanation'}
                            </button>
                            {showExplanation[q.question_id] && (
                                <div style={{ marginTop: '0.5rem', padding: '0.6rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '0.375rem', whiteSpace: 'pre-wrap' }} className="kp-read-explanation">
                                    {q.answer_explanation && <MathText text={q.answer_explanation} />}
                                    {q.explanation_image_url && (
                                        <ClickToViewImage src={q.explanation_image_url} containerStyle={{ marginTop: q.answer_explanation ? '0.5rem' : 0 }} />
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {renderQuestionStats(q.question_id)}
                </div>
            </div>
        );
    };

    const renderIeltsMatching = (qs) => {
        const head = qs.find(q => q.metadata && q.metadata.match_options) || qs[0];
        const box = (head && head.metadata && head.metadata.match_options) || {};
        const letters = Object.keys(box);
        return (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                {letters.length > 0 && (
                    <div style={{ background: '#f5f3ff', padding: '0.6rem 0.9rem', borderBottom: '1px solid #e9d5ff', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {letters.map(l => <span key={l} style={{ border: '1px solid #c4b5fd', background: '#fff', borderRadius: 6, padding: '2px 8px' }}><strong style={{ color: '#6d28d9' }}>{l}</strong> <MathText text={String(box[l])} /></span>)}
                    </div>
                )}
                <div style={{ padding: '0.4rem 0.9rem' }}>
                    {qs.map((q, i) => {
                        const no = (q.metadata && q.metadata.ielts && q.metadata.ielts.no) || (i + 1);
                        const ok = q.is_correct === true;
                        return (
                            <div key={q.question_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.35rem 0', borderBottom: i < qs.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                                <span style={{ color: ok ? '#16a34a' : '#dc2626', fontWeight: 800 }}>{ok ? '✓' : '✗'}</span>
                                <span style={{ fontWeight: 600, minWidth: '2rem' }}>{no}.</span>
                                <span style={{ flex: 1 }}><MathText text={q.question_text} /></span>
                                <span style={{ color: ok ? '#065f46' : '#991b1b', fontWeight: 700 }}>{q.selected_option || '—'}</span>
                                {!ok && <span style={{ color: '#065f46' }}>✓ {q.correct_option}</span>}
                                {questionControls(q)}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderIeltsTask = (task, ti) => {
        const qs = (task.question_ids || []).map(id => allById[id]).filter(Boolean);
        if (!qs.length) return null;
        let body;
        if (task.layout === 'notes' || task.template) {
            body = <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', whiteSpace: 'pre-wrap', lineHeight: 2.2, color: '#1f2937' }}>{reviewInlineTemplate(task.template, ieltsByNo(qs), `nb-${ti}`)}</div>;
        } else if (task.layout === 'table' || task.table) {
            const tbl = task.table || { headers: [], rows: [] };
            const byNo = ieltsByNo(qs);
            body = (
                <div style={{ overflowX: 'auto' }}>
                    <table className="table table-bordered align-middle" style={{ background: '#fff' }}>
                        {tbl.headers && tbl.headers.length > 0 && <thead className="table-light"><tr>{tbl.headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>}
                        <tbody>{(tbl.rows || []).map((row, ri) => <tr key={ri}>{row.map((cell, ci) => <td key={ci} style={{ whiteSpace: 'pre-wrap', verticalAlign: 'top' }}>{reviewInlineTemplate(cell, byNo, `tb-${ri}-${ci}`)}</td>)}</tr>)}</tbody>
                    </table>
                </div>
            );
        } else if (task.task === 'matching' || qs.every(q => q.question_type === 'MATCHING')) {
            body = renderIeltsMatching(qs);
        } else {
            body = qs.map((q, i) => renderIeltsQuestionCard(q, i));
        }
        // IELTS gap-fill word bank ("Reading-Summary (Word Bank)" / "Listening-Flow-chart
        // Completion") + Listening map/plan/diagram image ("Listening-Plan/Map/Diagram"): shown
        // once, read-only, below the task title and above the blanks — matching the taker.
        const wordBank = (qs.find(q => q.metadata && q.metadata.word_bank) || {}).metadata?.word_bank;
        const passageImage = (qs.find(q => q.metadata && q.metadata.passage_image) || {}).metadata?.passage_image;
        return (
            <div key={ti} style={{ marginBottom: '1.25rem' }}>
                {task.title && <h5 style={{ fontWeight: 700, color: '#1f2937', marginBottom: 4 }}><MathText text={task.title} /></h5>}
                {passageImage && (
                    <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
                        <ClickToViewImage src={passageImage} imgStyle={{ maxWidth: '100%', margin: '0 auto' }} />
                    </div>
                )}
                {wordBank && Object.keys(wordBank).length > 0 && (
                    <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '0.75rem' }}>
                        <div className="d-flex flex-wrap" style={{ gap: '0.35rem 1.5rem' }}>
                            {Object.keys(wordBank).map(l => (
                                <span key={l} style={{ color: '#4c1d95' }}><strong>{l}.</strong>{' '}<MathText text={String(wordBank[l])} /></span>
                            ))}
                        </div>
                    </div>
                )}
                {body}
            </div>
        );
    };

    const renderIeltsModule = (tasks, qs) => {
        const passage = (qs.find(q => q.side_text) || {}).side_text || '';
        const audio = (qs.find(q => q.audio_url) || {}).audio_url || '';
        return (
            <div style={{ paddingTop: '1rem' }}>
                {audio && <audio controls controlsList="nodownload" src={audio} style={{ width: '100%', marginBottom: '0.75rem' }} />}
                {passage && <div style={{ background: '#f3f4f6', borderLeft: '3px solid #9ca3af', padding: '0.6rem 0.8rem', borderRadius: 4, marginBottom: '0.9rem', whiteSpace: 'pre-wrap', color: '#374151' }}><MathText text={passage} /></div>}
                {tasks.map((t, ti) => renderIeltsTask(t, ti))}
            </div>
        );
    };

    // ---- TOEFL read-only review: mirror the taker (cloze / build-sentence / speaking / grouped MCQ),
    // numbered per-section (chronological) exactly as taken. ----
    const toeflExplanation = (q) => (
        (q.answer_explanation || q.explanation_image_url) && (
            <div>
                <button onClick={() => setShowExplanation(prev => ({ ...prev, [q.question_id]: !prev[q.question_id] }))}
                    style={{ backgroundColor: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: '0.375rem', padding: '0.3rem 0.6rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, marginTop: '0.4rem' }}>
                    {showExplanation[q.question_id] ? 'Hide Explanation' : 'Show Explanation'}
                </button>
                {showExplanation[q.question_id] && (
                    <div style={{ marginTop: '0.5rem', padding: '0.6rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '0.375rem', whiteSpace: 'pre-wrap' }} className="kp-read-explanation">
                        {q.answer_explanation && <MathText text={q.answer_explanation} />}
                        {q.explanation_image_url && (
                            <ClickToViewImage src={q.explanation_image_url} containerStyle={{ marginTop: q.answer_explanation ? '0.5rem' : 0 }} />
                        )}
                    </div>
                )}
            </div>
        )
    );

    // Complete-the-Words cloze: the passage with each {{n}} blank filled by the taker's answer,
    // coloured green/red, showing the correct answer when wrong. Numbered per-section.
    const renderToeflCloze = (qs) => {
        const sorted = [...qs].sort((a, b) => (a.group_order || 0) - (b.group_order || 0));
        const passage = (sorted[0] || {}).side_text || '';
        const byOrder = {};
        sorted.forEach((m, i) => { byOrder[m.group_order || (i + 1)] = m; });
        const parts = String(passage).split(/(\{\{\d+\}\})/g);
        let fallback = 0;
        return (
            <>
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', lineHeight: 2.4, color: '#1f2937' }}>
                    {parts.map((p, idx) => {
                        const mk = p.match(/^\{\{(\d+)\}\}$/);
                        if (!mk) return <MathText key={idx} text={p} />;
                        const n = Number(mk[1]);
                        const m = byOrder[n] || sorted[fallback++];
                        if (!m) return <span key={idx}>____</span>;
                        const no = numberByQid[m.question_id] || n;
                        const user = m.text_answer || '';
                        const correct = m.correct_text_answer || '';
                        const ok = m.is_correct === true;
                        const color = ok ? '#065f46' : '#991b1b';
                        const bg = ok ? '#d1fae5' : '#fee2e2';
                        return (
                            <span key={idx} style={{ display: 'inline-flex', alignItems: 'baseline', margin: '0 3px' }}>
                                <sup style={{ color: '#6d28d9', fontWeight: 700, marginRight: 2 }}>{no}</sup>
                                <span style={{ background: bg, color, borderRadius: 4, padding: '0 6px', fontWeight: 600, minWidth: '3rem', textAlign: 'left' }}>{user || '—'}</span>
                                {!ok && correct && <span style={{ color: '#065f46', marginLeft: 4, fontSize: '0.85rem' }}>✓ {correct}</span>}
                            </span>
                        );
                    })}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center', marginTop: '0.5rem' }}>
                    <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>Favorite / report a blank:</span>
                    {sorted.map(m => (
                        <span key={m.question_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, border: '1px solid #e5e7eb', borderRadius: 6, padding: '0 2px 0 6px' }}>
                            <span style={{ fontSize: '0.75rem', color: '#6d28d9', fontWeight: 700 }}>{numberByQid[m.question_id] || m.group_order}</span>
                            {questionControls(m)}
                        </span>
                    ))}
                </div>
            </>
        );
    };

    // Build-a-Sentence: the taker's arranged sentence vs the correct order (tiles from option fields;
    // selected_option = ordered original tile indices, metadata.correct_order = the answer sequence).
    // `displayNo` is a fallback number for quizzes without a blueprint (e.g. the daily quiz).
    const renderToeflBuildSentence = (q, displayNo) => {
        const tiles = OPTION_KEYS.map(k => q[k]).filter(t => t && String(t).trim());
        const userIdx = String(q.selected_option || '').split(',').map(s => s.trim()).filter(s => s !== '').map(Number);
        const correctIdx = ((q.metadata && q.metadata.correct_order) || []);
        const userSentence = userIdx.map(i => tiles[i]).filter(Boolean).join(' ');
        const correctSentence = correctIdx.map(i => tiles[i]).filter(Boolean).join(' ');
        const ok = q.is_correct === true;
        const no = numberByQid[q.question_id] || displayNo;
        return (
            <div key={q.question_id} style={{ marginBottom: '1rem', padding: '0.75rem', border: '1px solid #eef2ff', borderRadius: '0.5rem', background: '#f9fafb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <p style={{ color: '#1f2937', margin: 0, flex: 1 }}>
                        <span style={{ color: ok ? '#16a34a' : '#dc2626', fontWeight: 800, fontSize: '1.2rem', marginRight: 6, verticalAlign: 'middle' }}>{ok ? '✓' : '✗'}</span>
                        {no ? <span style={{ fontWeight: 700, color: '#f97316', marginRight: 6 }}>Question {no}</span> : null}<MathText text={q.question_text} />
                    </p>
                    {questionControls(q)}
                </div>
                <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    <div style={{ padding: '0.5rem', borderRadius: '0.25rem', backgroundColor: ok ? '#d1fae5' : '#fee2e2', color: ok ? '#065f46' : '#991b1b' }}>
                        <strong>Your Answer: </strong>{userSentence ? <MathText text={userSentence} /> : <span style={{ color: '#6b7280' }}>No answer</span>}
                    </div>
                    {!ok && (
                        <div style={{ padding: '0.5rem', borderRadius: '0.25rem', backgroundColor: '#d1fae5', color: '#065f46' }}><strong>Correct Answer: </strong><MathText text={correctSentence} /></div>
                    )}
                    {toeflExplanation(q)}
                </div>
            </div>
        );
    };

    // Speaking: recorded transcript, not scored.
    const renderToeflSpeaking = (q) => {
        const no = numberByQid[q.question_id];
        return (
            <div key={q.question_id} style={{ marginBottom: '1rem', padding: '0.75rem', border: '1px solid #eef2ff', borderRadius: '0.5rem', background: '#f9fafb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <p style={{ fontWeight: 'bold', color: '#1f2937', margin: 0, flex: 1 }}>{no ? `${no}. ` : ''}Speaking response</p>
                    {questionControls(q)}
                </div>
                {q.audio_url && <audio controls controlsList="nodownload" src={q.audio_url} style={{ width: '100%', margin: '0.5rem 0' }} />}
                <div style={{ padding: '0.5rem', borderRadius: '0.25rem', backgroundColor: '#eef2ff', color: '#1f2937', whiteSpace: 'pre-wrap' }}>
                    <strong>Your transcript: </strong>{q.text_answer ? <MathText text={q.text_answer} /> : <span style={{ color: '#6b7280' }}>No response</span>}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.3rem' }}>Speaking is recorded but not scored.</div>
            </div>
        );
    };

    // MCQ / writing card for the TOEFL review, numbered per-section. Clusters share the passage/audio
    // once (rendered by the caller).
    const renderToeflQuestionCard = (q) => {
        const ok = q.is_correct === true;
        const isText = q.question_type === 'TEXT';
        const autoGradedText = isText && !!q.correct_text_answer;
        const wrong = q.is_correct === false || (q.is_correct == null && (!isText || autoGradedText));
        const no = numberByQid[q.question_id];
        return (
            <div key={q.question_id} style={{ marginBottom: '1rem', padding: '0.75rem', border: '1px solid #eef2ff', borderRadius: '0.5rem', background: '#f9fafb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <p style={{ color: '#1f2937', margin: 0, flex: 1 }}>
                        {(ok || wrong) && <span style={{ color: ok ? '#16a34a' : '#dc2626', fontWeight: 800, fontSize: '1.2rem', marginRight: 6, verticalAlign: 'middle' }}>{ok ? '✓' : '✗'}</span>}
                        {no ? <span style={{ fontWeight: 700, color: '#f97316', marginRight: 6 }}>Question {no}</span> : null}<MathText text={q.question_text} />
                    </p>
                    {questionControls(q)}
                </div>
                <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {isText ? (
                        (() => {
                            const wrongOrPending = q.is_correct == null && !autoGradedText;
                            const bg = wrongOrPending ? '#eef2ff' : (ok ? '#d1fae5' : '#fee2e2');
                            const fg = wrongOrPending ? '#1f2937' : (ok ? '#065f46' : '#991b1b');
                            return (
                                <>
                                    <div style={{ padding: '0.5rem', borderRadius: '0.25rem', backgroundColor: bg, color: fg }}>
                                        <strong>Your Answer: </strong>{q.text_answer ? <MathText text={q.text_answer} /> : <span style={{ color: '#6b7280' }}>No answer</span>}
                                    </div>
                                    {q.correct_text_answer && (
                                        <div style={{ padding: '0.5rem', borderRadius: '0.25rem', backgroundColor: '#d1fae5', color: '#065f46' }}><strong>Correct Answer: </strong><MathText text={q.correct_text_answer} /></div>
                                    )}
                                    {wrongOrPending && <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Text answers are reviewed manually.</div>}
                                </>
                            );
                        })()
                    ) : (
                        <>
                            {OPTION_KEYS.map(optionKey => q[optionKey] && (
                                <div key={optionKey} style={getOptionStyle(optionKey, q)}>
                                    <span style={{ fontWeight: 'bold' }}>{optionKey.replace('option_', '').toUpperCase()}: </span><MathText text={q[optionKey]} />
                                </div>
                            ))}
                            <p style={{ marginTop: '0.25rem', fontSize: '0.85rem' }}>
                                <strong style={{ color: '#4b5563' }}>Your Answer: </strong>
                                <span style={{ color: q.selected_option ? (ok ? '#065f46' : '#991b1b') : '#dc2626', fontWeight: 700 }}>{q.selected_option || 'No selection'}</span>
                                {' · '}<strong style={{ color: '#4b5563' }}>Correct: </strong><span style={{ color: '#065f46' }}>{q.correct_option}</span>
                            </p>
                        </>
                    )}
                    {toeflExplanation(q)}
                </div>
            </div>
        );
    };

    const renderToeflTask = (task, ti) => {
        const qs = (task.question_ids || []).map(id => allById[id]).filter(Boolean);
        if (!qs.length) return null;
        let body;
        if (task.task === 'complete_words') {
            body = renderToeflCloze(qs);
        } else if (task.task === 'build_sentence') {
            body = qs.map(q => renderToeflBuildSentence(q));
        } else if (qs.some(q => q.metadata && q.metadata.toefl && q.metadata.toefl.capture === 'speaking')) {
            body = qs.map(q => renderToeflSpeaking(q));
        } else {
            // MCQ (and writing TEXT): cluster by group_id so a shared audio clip / reading passage
            // renders once, matching the taker.
            const clusters = [];
            qs.forEach(q => {
                const last = clusters[clusters.length - 1];
                if (q.group_id && last && last.gid === q.group_id) last.items.push(q);
                else clusters.push({ gid: q.group_id || null, items: [q] });
            });
            body = clusters.map((c, ci) => {
                const head = c.items[0];
                return (
                    <div key={`g-${c.gid}-${ci}`}>
                        {c.gid && head.audio_url && <audio controls controlsList="nodownload" src={head.audio_url} style={{ width: '100%', marginBottom: '0.5rem' }} />}
                        {c.gid && head.side_text && <div style={{ background: '#f3f4f6', borderLeft: '3px solid #9ca3af', padding: '0.6rem 0.8rem', borderRadius: 4, marginBottom: '0.6rem', whiteSpace: 'pre-wrap', color: '#374151' }}><MathText text={head.side_text} /></div>}
                        {c.items.map(q => renderToeflQuestionCard(q))}
                    </div>
                );
            });
        }
        return (
            <div key={ti} style={{ marginBottom: '1.25rem' }}>
                {task.title && <h5 style={{ fontWeight: 700, color: '#1f2937', marginBottom: 4 }}><MathText text={task.title} /></h5>}
                {body}
            </div>
        );
    };

    const renderToeflModule = (tasks) => (
        <div style={{ paddingTop: '1rem' }}>
            {tasks.map((t, ti) => renderToeflTask(t, ti))}
        </div>
    );

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: '84px',
            zIndex: 1100
        }}>
            <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '0.75rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
                width: '96%',
                maxWidth: 'min(1500px, 97vw)',
                position: 'relative',
                height: '88vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                <div style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff', padding: '1.1rem 1.25rem', position: 'relative', flexShrink: 0 }}>
                    <CloseButton onClick={onClose} />
                    <h2 style={{ fontSize: '1.35rem', fontWeight: 700, margin: 0 }}>Test Submission Details</h2>
                </div>
                <div style={{ overflowY: 'auto', padding: '1.5rem 2rem', flex: 1 }}>

                {/* Report summary */}
                <div style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', backgroundColor: '#f8fafc' }}>
                    <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                        <span style={{ display: 'inline-block', fontSize: '1.25rem', fontWeight: 700, padding: '0.4rem 1rem', borderRadius: '9999px', color: '#fff', backgroundColor: accuracyColor(overallPct) }}>
                            Score: {totalCorrect} / {totalQ} ({overallPct}%)
                        </span>
                        <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.4rem' }}>
                            Answered {totalAnswered} of {totalQ} questions
                        </div>
                    </div>
                    {toeflPerf && (() => {
                        // The 0–30 total maxes at 30 per SCORED section (a section with a numeric
                        // scaled value; Speaking is unscored → null). So a subject-wise micro (one
                        // scored section) reads / 30, a full test / (scored sections × 30) — not a
                        // hardcoded 120, which over-counts unscored sections.
                        const toeflScaledMax = (toeflPerf.sections || []).filter(s => s.scaled != null).length * 30;
                        return (
                        <div style={{ marginBottom: '1rem', padding: '0.9rem 1rem', border: '1px solid #ddd6fe', borderRadius: '0.5rem', backgroundColor: '#faf5ff' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center', marginBottom: '0.75rem' }}>
                                <span style={{ fontSize: '1rem', fontWeight: 700, padding: '0.35rem 0.9rem', borderRadius: '9999px', color: '#fff', backgroundColor: '#7c3aed' }}>
                                    Overall: {toeflPerf.total_band != null ? toeflPerf.total_band.toFixed(1) : '—'} / 6.0
                                </span>
                                <span style={{ fontSize: '1rem', fontWeight: 700, padding: '0.35rem 0.9rem', borderRadius: '9999px', color: '#fff', backgroundColor: '#6b7280' }}>
                                    {toeflPerf.total_scaled} / {toeflScaledMax}
                                </span>
                            </div>
                            <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ color: '#6b7280', textAlign: 'left' }}>
                                        <th style={{ padding: '0.25rem 0.4rem' }}>Section</th>
                                        <th style={{ padding: '0.25rem 0.4rem', textAlign: 'center' }}>Raw</th>
                                        <th style={{ padding: '0.25rem 0.4rem', textAlign: 'center' }}>0–30</th>
                                        <th style={{ padding: '0.25rem 0.4rem', textAlign: 'center' }}>1–6</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(toeflPerf.sections || []).map(s => (
                                        <tr key={s.name} style={{ borderTop: '1px solid #ede9fe' }}>
                                            <td style={{ padding: '0.3rem 0.4rem' }}>{s.name}</td>
                                            <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>{s.unscored ? '—' : `${s.raw}/${s.max}`}</td>
                                            <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>{s.unscored ? 'Not scored' : (s.scaled != null ? s.scaled : '—')}</td>
                                            <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>{s.unscored ? '—' : (s.band != null ? s.band.toFixed(1) : '—')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr style={{ borderTop: '2px solid #ddd6fe', fontWeight: 700 }}>
                                        <td style={{ padding: '0.3rem 0.4rem' }}>Total</td>
                                        <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>{toeflPerf.total_raw}/{toeflPerf.total_max}</td>
                                        <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>{toeflPerf.total_scaled}/{toeflScaledMax}</td>
                                        <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>{toeflPerf.total_band != null ? toeflPerf.total_band.toFixed(1) : '—'}</td>
                                    </tr>
                                </tfoot>
                            </table>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                                Both TOEFL scales: legacy 0–30 per scored section (a full test sums to 120) and the 1–6 band (overall = average of the scored sections, nearest half-band). Speaking (and Writing email/discussion) are recorded but not scored.
                            </div>
                        </div>
                        );
                    })()}
                    {ieltsPerf && (
                        <div style={{ marginBottom: '1rem', padding: '0.9rem 1rem', border: '1px solid #ddd6fe', borderRadius: '0.5rem', backgroundColor: '#faf5ff' }}>
                            <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
                                <span style={{ fontSize: '1rem', fontWeight: 700, padding: '0.35rem 0.9rem', borderRadius: '9999px', color: '#fff', backgroundColor: '#7c3aed' }}>
                                    Overall band: {ieltsPerf.total_band != null ? ieltsPerf.total_band.toFixed(1) : '—'} / 9.0
                                </span>
                            </div>
                            <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ color: '#6b7280', textAlign: 'left' }}>
                                        <th style={{ padding: '0.25rem 0.4rem' }}>Section</th>
                                        <th style={{ padding: '0.25rem 0.4rem', textAlign: 'center' }}>Raw</th>
                                        <th style={{ padding: '0.25rem 0.4rem', textAlign: 'center' }}>Band (0–9)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(ieltsPerf.sections || []).map(s => (
                                        <tr key={s.name} style={{ borderTop: '1px solid #ede9fe' }}>
                                            <td style={{ padding: '0.3rem 0.4rem' }}>{s.name}</td>
                                            <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>{s.unscored ? '—' : `${s.raw}/${s.max}`}</td>
                                            <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>{s.unscored ? 'Not scored' : (s.band != null ? s.band.toFixed(1) : '—')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr style={{ borderTop: '2px solid #ddd6fe', fontWeight: 700 }}>
                                        <td style={{ padding: '0.3rem 0.4rem' }}>Total</td>
                                        <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>{ieltsPerf.total_raw}/{ieltsPerf.total_max}</td>
                                        <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>{ieltsPerf.total_band != null ? ieltsPerf.total_band.toFixed(1) : '—'}</td>
                                    </tr>
                                </tfoot>
                            </table>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                                Listening &amp; Reading are band-scored (0–9) from the official raw→band tables; the overall band is the average of the scored sections (nearest half-band). Writing &amp; Speaking are recorded but not scored.
                            </div>
                        </div>
                    )}
                    {satPerf && (() => {
                        // Max counts only modules that carry scoreable questions (max > 0). An empty
                        // module scores 0, so it must not add 400 to the denominator — a topic-wise
                        // test (one real module) reads / 400, a full 4-module SAT reads / 1600.
                        const satScoredCount = (satPerf.modules || []).filter(m => (m.max || 0) > 0).length;
                        const satMax = satScoredCount * 400;
                        return (
                        <div style={{ marginBottom: '1rem', padding: '0.9rem 1rem', border: '1px solid #ddd6fe', borderRadius: '0.5rem', backgroundColor: '#faf5ff' }}>
                            <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
                                <span style={{ fontSize: '1rem', fontWeight: 700, padding: '0.35rem 0.9rem', borderRadius: '9999px', color: '#fff', backgroundColor: '#7c3aed' }}>
                                    Total score: {satPerf.total_scaled} / {satMax}
                                </span>
                            </div>
                            <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ color: '#6b7280', textAlign: 'left' }}>
                                        <th style={{ padding: '0.25rem 0.4rem' }}>Section</th>
                                        <th style={{ padding: '0.25rem 0.4rem' }}>Module</th>
                                        <th style={{ padding: '0.25rem 0.4rem', textAlign: 'center' }}>Correct</th>
                                        <th style={{ padding: '0.25rem 0.4rem', textAlign: 'center' }}>Scaled / 400</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(satPerf.modules || []).map((m, i) => (
                                        <tr key={i} style={{ borderTop: '1px solid #ede9fe' }}>
                                            <td style={{ padding: '0.3rem 0.4rem' }}>{m.section}</td>
                                            <td style={{ padding: '0.3rem 0.4rem' }}>{m.module}</td>
                                            <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>{m.raw}/{m.max}</td>
                                            <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>{m.scaled}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr style={{ borderTop: '2px solid #ddd6fe', fontWeight: 700 }}>
                                        <td style={{ padding: '0.3rem 0.4rem' }} colSpan={3}>Total</td>
                                        <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>{satPerf.total_scaled} / {satMax}</td>
                                    </tr>
                                </tfoot>
                            </table>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                                Each module is scaled to 400 (correct ÷ scoreable); a full SAT has four modules summing to 1600.
                            </div>
                        </div>
                        );
                    })()}
                    {grePerf && (() => {
                        // Full published GRE test (4 scored modules) shows the /340 total (260 base +
                        // 4×20); a topic-wise (single-module) test shows just raw + /20, no total.
                        const isFull = grePerf.scored_count >= 4;
                        return (
                        <div style={{ marginBottom: '1rem', padding: '0.9rem 1rem', border: '1px solid #ddd6fe', borderRadius: '0.5rem', backgroundColor: '#faf5ff' }}>
                            {isFull && (
                                <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
                                    <span style={{ fontSize: '1rem', fontWeight: 700, padding: '0.35rem 0.9rem', borderRadius: '9999px', color: '#fff', backgroundColor: '#7c3aed' }}>
                                        Total score: {grePerf.total_scaled} / 340
                                    </span>
                                </div>
                            )}
                            <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ color: '#6b7280', textAlign: 'left' }}>
                                        <th style={{ padding: '0.25rem 0.4rem' }}>Section</th>
                                        <th style={{ padding: '0.25rem 0.4rem', textAlign: 'center' }}>Correct</th>
                                        <th style={{ padding: '0.25rem 0.4rem', textAlign: 'center' }}>Scaled / 20</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(grePerf.sections || []).map((s, i) => (
                                        <tr key={i} style={{ borderTop: '1px solid #ede9fe' }}>
                                            <td style={{ padding: '0.3rem 0.4rem' }}>{s.name}</td>
                                            <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>{s.unscored ? '—' : `${s.raw}/${s.max}`}</td>
                                            <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>{s.unscored ? 'Not scored' : s.scaled}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                {isFull && (
                                    <tfoot>
                                        <tr style={{ borderTop: '2px solid #ddd6fe', fontWeight: 700 }}>
                                            <td style={{ padding: '0.3rem 0.4rem' }}>Total (260 base + 4×20)</td>
                                            <td style={{ padding: '0.3rem 0.4rem' }}></td>
                                            <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>{grePerf.total_scaled} / 340</td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                                {isFull
                                    ? 'Each scored module is scaled to 20; the four are added to a 260 base (max 340). The Analytical Writing essay is not auto-scored.'
                                    : 'This module is scaled to 20 (correct ÷ scoreable).'}
                            </div>
                        </div>
                        );
                    })()}
                    {subjectStats.length > 0 && (
                        <div>
                            <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#374151', marginBottom: '0.5rem' }}>By subject</h4>
                            {subjectStats.map(s => (
                                <div key={s.subject} style={{ marginBottom: '0.5rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                                        <span>{s.subject}</span>
                                        <span>{s.correct}/{s.total} ({s.accuracy}%)</span>
                                    </div>
                                    <div style={{ height: 8, backgroundColor: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                                        <div style={{ width: `${s.accuracy}%`, height: '100%', backgroundColor: accuracyColor(s.accuracy) }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {weakSubjects.length > 0 && (
                        <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.8rem', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '0.375rem', fontSize: '0.85rem', color: '#92400e' }}>
                            <strong>Recommended focus:</strong> {weakSubjects.map(s => `${s.subject} (${s.accuracy}%)`).join(', ')}
                        </div>
                    )}
                </div>

                {reviewGroups.map(({ key: groupKey, label, questions, ieltsTasks, toeflTasks }) => (
                    <div key={groupKey} style={{ marginBottom: '1.5rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem' }}>
                        <div
                            onClick={() => toggleSubject(groupKey)}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: 'pointer',
                                paddingBottom: '0.5rem',
                                borderBottom: openSubjects[groupKey] !== false ? '1px solid #e5e7eb' : 'none'
                            }}
                        >
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937' }}>{label} ({groupCount(questions)} questions)</h3>
                            <span>{openSubjects[groupKey] !== false ? '▲' : '▼'}</span>
                        </div>
                        {openSubjects[groupKey] !== false && (ieltsTasks ? renderIeltsModule(ieltsTasks, questions) : toeflTasks ? renderToeflModule(toeflTasks) : (
                            <div style={{ paddingTop: '1rem' }}>
                                {questions.map((q, qIndex) => {
                                    // Cluster passage groups: show the shared passage/audio once,
                                    // at the first member (backend keeps members contiguous+ordered).
                                    const prevQ = qIndex > 0 ? questions[qIndex - 1] : null;
                                    const isGroupStart = q.group_id
                                        ? (!prevQ || prevQ.group_id !== q.group_id)
                                        : true;
                                    // Present these exactly like the quiz-taking modal (not the flat
                                    // MCQ card): Build-a-Sentence as the arranged sentence, and a
                                    // "Complete the Words" cloze group as the passage with filled
                                    // inline blanks (rendered once at the group's first member).
                                    if (q.question_type === 'BUILD_SENTENCE') {
                                        return renderToeflBuildSentence(q, qIndex + 1);
                                    }
                                    const isClozeMember = q.question_type === 'TEXT' && /\{\{\d+\}\}/.test(q.side_text || '');
                                    if (isClozeMember && q.group_id) {
                                        if (!isGroupStart) return null; // rendered with the group's first member
                                        const members = [];
                                        for (let k = qIndex; k < questions.length && questions[k].group_id === q.group_id; k++) members.push(questions[k]);
                                        return (
                                            <div key={q.question_id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #e9d5ff', borderRadius: '0.5rem', backgroundColor: '#faf5ff' }}>
                                                {renderToeflCloze(members)}
                                            </div>
                                        );
                                    }
                                    // Matching: render the shared option box + each item's picked vs
                                    // correct once for the run of consecutive matching questions, like
                                    // the taking modal (rather than an empty MCQ card).
                                    if (q.question_type === 'MATCHING') {
                                        if (prevQ && prevQ.question_type === 'MATCHING') return null; // rendered with the run's first item
                                        const members = [];
                                        for (let k = qIndex; k < questions.length && questions[k].question_type === 'MATCHING'; k++) members.push(questions[k]);
                                        return (
                                            <div key={q.question_id} style={{ marginBottom: '1.5rem' }}>
                                                {renderIeltsMatching(members)}
                                            </div>
                                        );
                                    }
                                    return (
                                    <div key={q.question_id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #eff6ff', borderRadius: '0.5rem', backgroundColor: '#f9fafb' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                                            <div style={{ flex: 1 }}>
                                                {isGroupStart && q.side_text && (
                                                    <div style={{ background: '#f3f4f6', borderLeft: '3px solid #9ca3af', padding: '0.5rem 0.75rem', borderRadius: '0.25rem', marginBottom: '0.5rem', whiteSpace: 'pre-wrap', color: '#374151' }}><MathText text={q.side_text} /></div>
                                                )}
                                                {isGroupStart && q.audio_url && (
                                                    <audio controls controlsList="nodownload" src={q.audio_url} style={{ width: '100%', marginBottom: '0.5rem' }} />
                                                )}
                                                {q.question_image_url && (
                                                    <ClickToViewImage src={q.question_image_url} />
                                                )}
                                                <p style={{ color: '#1f2937', margin: 0 }}>
                                                    {(() => {
                                                        // Auto-graded TEXT (Complete-the-Words blanks) carry a reference answer,
                                                        // so an unanswered one (is_correct null, no answer row) is WRONG → red cross.
                                                        // Only reference-less TEXT (writing/speaking) stays unmarked (manual/ungraded).
                                                        const autoGradedText = q.question_type === 'TEXT' && !!q.correct_text_answer;
                                                        const isCorrect = q.is_correct === true;
                                                        const isWrong = q.is_correct === false
                                                            || (q.is_correct == null && (q.question_type !== 'TEXT' || autoGradedText));
                                                        if (!isCorrect && !isWrong) return null;
                                                        return (
                                                            <span style={{ color: isCorrect ? '#16a34a' : '#dc2626', fontWeight: 800, fontSize: '1.3rem', marginRight: '0.4rem', verticalAlign: 'middle' }}>
                                                                {isCorrect ? '✓' : '✗'}
                                                            </span>
                                                        );
                                                    })()}
                                                    {qIndex + 1 ? <span style={{ fontWeight: 700, color: '#f97316', marginRight: 6 }}>Question {qIndex + 1}</span> : null}<MathText text={(q.question_text || '').replace(/\s*[—-]\s*m\d+\s*$/i, '')} />
                                                </p>
                                            </div>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                {/* Star button for favorites */}
                                                <button 
                                                    onClick={() => toggleFavorite(q.question_id)}
                                                    disabled={loadingFavorites[q.question_id]}
                                                    style={{
                                                        backgroundColor: 'transparent',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        padding: '0.3rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        opacity: loadingFavorites[q.question_id] ? 0.7 : 1
                                                    }}
                                                    title={favoriteQuestionDetails[q.question_id] ? "Remove from favorites" : "Add to favorites"}
                                                >
                                                    <Star 
                                                        fill={favoriteQuestionDetails[q.question_id] ? "#fbbf24" : "transparent"} 
                                                        color={favoriteQuestionDetails[q.question_id] ? "#fbbf24" : "#6b7280"} 
                                                        size={20} 
                                                    />
                                                </button>
                                                <ReportQuestionDropdown questionId={q.question_id} questionReportDetails={reportedQuestionDetails[q.question_id] || {}} onClose={onClose} />
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            {q.question_type === 'TEXT' ? (
                                                (() => {
                                                    // Referenced TEXT (Complete-the-Words) is auto-graded: null (unanswered) = wrong (red).
                                                    // Reference-less TEXT (writing/speaking) is manual: null = pending (blue).
                                                    const autoGradedText = !!q.correct_text_answer;
                                                    const wrongOrPending = q.is_correct == null && !autoGradedText;
                                                    const bg = wrongOrPending ? '#eef2ff' : (q.is_correct ? '#d1fae5' : '#fee2e2');
                                                    const fg = wrongOrPending ? '#1f2937' : (q.is_correct ? '#065f46' : '#991b1b');
                                                    return (
                                                        <>
                                                            <div style={{ padding: '0.5rem', borderRadius: '0.25rem', backgroundColor: bg, color: fg }}>
                                                                <strong>Your Answer: </strong>
                                                                {q.text_answer ? <MathText text={q.text_answer} /> : <span style={{ color: '#6b7280' }}>No answer</span>}
                                                            </div>
                                                            {q.correct_text_answer && (
                                                                <div style={{ padding: '0.5rem', borderRadius: '0.25rem', backgroundColor: '#d1fae5', color: '#065f46' }}>
                                                                    <strong>Correct Answer: </strong><MathText text={q.correct_text_answer} />
                                                                </div>
                                                            )}
                                                            {wrongOrPending && (
                                                                <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Text answers are reviewed manually.</div>
                                                            )}
                                                        </>
                                                    );
                                                })()
                                            ) : q.question_type === 'TEXT_COMPLETION' ? (
                                                <>
                                                    {(() => {
                                                        // selected_option is ordered per-blank indices, e.g. "1,0".
                                                        const picks = String(q.selected_option || '').split(',').map(s => s.trim()).filter(s => s !== '').map(Number);
                                                        const blanks = (q.metadata && q.metadata.blanks) || [];
                                                        return blanks.map((blank, bi) => {
                                                            const userIdx = picks[bi];
                                                            return (
                                                                <div key={bi} style={{ marginBottom: '0.5rem' }}>
                                                                    <div style={{ fontWeight: 600, color: '#4b5563', marginBottom: '0.25rem' }}>Blank {TC_BLANK_LABELS[bi] || `#${bi + 1}`}</div>
                                                                    {(blank.choices || []).map((choice, ci) => {
                                                                        const isCorrect = blank.correct === ci;
                                                                        const isPicked = userIdx === ci;
                                                                        const style = { padding: '0.4rem 0.6rem', borderRadius: '0.25rem', marginBottom: 2 };
                                                                        if (isCorrect) { style.backgroundColor = '#d1fae5'; style.color = '#065f46'; }
                                                                        else if (isPicked) { style.backgroundColor = '#fee2e2'; style.color = '#991b1b'; }
                                                                        return (
                                                                            <div key={ci} style={style}>
                                                                                <span style={{ fontWeight: 'bold' }}>{String.fromCharCode(65 + ci)}: </span>
                                                                                <MathText text={choice} />
                                                                                {isCorrect && <span style={{ marginLeft: 6, fontSize: '0.75rem' }}>✓ correct</span>}
                                                                                {isPicked && !isCorrect && <span style={{ marginLeft: 6, fontSize: '0.75rem' }}>your pick</span>}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                    {userIdx == null && <div style={{ fontSize: '0.8rem', color: '#dc2626', fontWeight: 700 }}>No selection</div>}
                                                                </div>
                                                            );
                                                        });
                                                    })()}
                                                </>
                                            ) : (
                                                <>
                                                    {OPTION_KEYS.map(optionKey => (
                                                        q[optionKey] && (
                                                            <div key={optionKey} style={getOptionStyle(optionKey, q)}>
                                                                <span style={{ fontWeight: 'bold' }}>{optionKey.replace('option_', '').toUpperCase()}: </span>
                                                                {q[`${optionKey}_image_url`] && (
                                                                    <ClickToViewImage src={q[`${optionKey}_image_url`]} imgStyle={{ height: 170 }} />
                                                                )}
                                                                <MathText text={q[optionKey]} />
                                                            </div>
                                                        )
                                                    ))}
                                                    <p style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                                                        <strong style={{ color: '#4b5563' }}>Your Answer: </strong>
                                                        <span style={{
                                                            color: q.selected_option ? (q.is_correct ? '#065f46' : '#991b1b') : '#dc2626',
                                                            fontWeight: (!q.selected_option || !q.is_correct) ? 700 : 400,
                                                        }}>
                                                            {q.selected_option ? q.selected_option : 'No selection'}
                                                        </span>
                                                        <br />
                                                <strong style={{ color: '#4b5563' }}>Correct Answer: </strong>
                                                <span style={{ color: '#065f46' }}>{q.correct_option}</span>
                                            </p>
                                                </>
                                            )}
                                            {(q.answer_explanation || q.explanation_image_url) && (
                                                <div>
                                                    <button
                                                        onClick={() => setShowExplanation(prev => ({ ...prev, [q.question_id]: !prev[q.question_id] }))}
                                                        style={{ display: 'block', width: '12.5rem', textAlign: 'center', backgroundColor: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: '0.375rem', padding: '0.35rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
                                                    >
                                                        {showExplanation[q.question_id] ? 'Hide Explanation' : 'Show Explanation'}
                                                    </button>
                                                    {showExplanation[q.question_id] && (
                                                        <div style={{ marginTop: '0.5rem', padding: '0.75rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '0.375rem', whiteSpace: 'pre-wrap' }} className="kp-read-explanation">
                                                            {q.answer_explanation && <MathText text={q.answer_explanation} />}
                                                            {q.explanation_image_url && (
                                                                <ClickToViewImage src={q.explanation_image_url} containerStyle={{ marginTop: q.answer_explanation ? '0.5rem' : 0 }} />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {/* Practice more of the same — not for IELTS/TOEFL (section/audio model tests). */}
                                            {!(isIelts || isToefl) && (
                                                <button
                                                    onClick={() => setPracticeForQuestionId(q.question_id)}
                                                    style={{ display: 'block', width: '12.5rem', textAlign: 'center', marginTop: '0.5rem', backgroundColor: '#f5f3ff', color: '#5b21b6', border: '1px solid #ddd6fe', borderRadius: '0.375rem', padding: '0.35rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
                                                >
                                                    Practice Similar Question
                                                </button>
                                            )}
                                            {renderQuestionStats(q.question_id)}
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                ))}
                </div>
            </div>
            {practiceForQuestionId && (
                <PracticeSimilarQuestionsModal
                    sourceQuestionId={practiceForQuestionId}
                    onClose={() => setPracticeForQuestionId(null)}
                />
            )}
        </div>
    );
};

export default QuizSubmissionDetailsModal;
